import 'css/src/global';

import React, { useEffect } from 'react';
import type { ConnectedProps } from 'react-redux';
import { useSelector } from 'react-redux';
import type { OpaqueVideoData } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';

import { VideoStatus } from 'js/src/client';
import VideoPreview from 'js/src/components/common/video-preview/VideoPreview';
import { loadVideo } from 'js/src/data/share-video';
import { getAbsoluteUrl } from 'js/src/libs/custom-utils';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';
import {
    getLuminaryResolutionFromAvailScreenWidth,
    LuminaryResolutionPreset,
} from 'js/src/libs/video-data-consts';
import {
    LUMINARY_VIDEO_STORE_NAME,
    LuminaryContextProvider,
    luminaryReduxConnect,
} from 'js/src/luminary';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

type EmbedVideoPropsFromRedux = ConnectedProps<typeof connector>;

const EmbedVideo = React.memo(function EmbedVideo({
    clearAndLoadLuminary,
}: EmbedVideoPropsFromRedux) {
    const dispatch = useAppDispatch();
    const video = useSelector((state: RootState) => state.shareVideo.video);
    const loading = useSelector((state: RootState) => state.shareVideo.loading);
    const [loadingError, setLoadingError] = React.useState<string | null>(null);

    useEffect(() => {
        const doTheLoad = async () => {
            try {
                await dispatch(loadVideo());
            } catch (error) {
                setLoadingError('Error loading video');
            }
        };
        void doTheLoad();
    }, [dispatch]);

    useEffect(() => {
        if (video.size) {
            clearAndLoadLuminary(video.get('video_data').toJS());
        }
    }, [clearAndLoadLuminary, video]);

    if (loading || !video.size) {
        return (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                <h4>Loading…</h4>
            </div>
        );
    }
    if (loadingError) {
        return (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                <h4>{loadingError}</h4>
            </div>
        );
    }

    const videoRenders = video.get('video_renders');
    const videoStatus = video.get('status');
    const sortedVideoSourceList =
        videoRenders.size > 1
            ? videoRenders.sortBy((segment) => segment.get('series_index'))
            : videoRenders;
    const sharePageUrl = getAbsoluteUrl(video.get('share_link'));

    return (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <VideoPreview
                autoplay={false}
                startMuted={false}
                showMuteIndicator={true}
                enableKeyboardShortcuts={true}
                sortedVideoSourceList={sortedVideoSourceList}
                isHtmlVideo={videoStatus === VideoStatus.PUBLISHED}
                shareLink={sharePageUrl}
            />
        </div>
    );
});

const mapLuminaryOperationsToProps = (
    _dispatch: AppDispatch,
    _getState: GetRootState,
    luminary: Luminary,
) => ({
    clearAndLoadLuminary: async (videoData: OpaqueVideoData) => {
        await luminary.clear();
        luminary.load(videoData);
        await luminary.onCanPlay();
        await luminary.startDrawingToCanvas();
    },
});

const connector = luminaryReduxConnect(
    undefined,
    undefined,
    undefined,
    mapLuminaryOperationsToProps,
);
const EmbedVideoConnected = connector(EmbedVideo);

const EmbedVideoPage = React.memo(function EmbedVideoPage() {
    return (
        <LuminaryContextProvider
            minDimensionResolution={getLuminaryResolutionFromAvailScreenWidth(
                LuminaryResolutionPreset.HIGH,
            )}
            drawsToCanvas={true}
            name={LUMINARY_VIDEO_STORE_NAME}
        >
            <EmbedVideoConnected />
        </LuminaryContextProvider>
    );
});

export default EmbedVideoPage;
